import { forwardRef, memo, type TextareaHTMLAttributes, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { getAccessibleFormProps, getFocusClasses } from '../../utils/accessibility';
import type { BaseComponentProps } from '../../types';
import type { AccessibleFormProps } from '../../types/accessibility.types';

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    BaseComponentProps {
  /**
   * Label del campo
   */
  label?: string;
  /**
   * Stato di errore
   */
  error?: boolean;
  /**
   * Messaggio di errore
   */
  errorMessage?: string;
  /**
   * Testo di aiuto
   */
  helpText?: string;
  /**
   * Auto-resize in base al contenuto
   */
  autoResize?: boolean;
  /**
   * Mostra contatore caratteri
   */
  showCount?: boolean;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Componente Textarea
 * 
 * Campo textarea con supporto per auto-resize, contatore caratteri e accessibilità completa.
 * 
 * @example
 * ```tsx
 * <Textarea label="Messaggio" placeholder="Scrivi il tuo messaggio..." />
 * <Textarea label="Bio" maxLength={200} showCount autoResize />
 * <Textarea label="Note" error errorMessage="Campo richiesto" />
 * ```
 */
const TextareaComponent = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      errorMessage,
      helpText,
      autoResize,
      showCount,
      size = 'md',
      required,
      maxLength,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const [charCount, setCharCount] = useState(0);

    const { inputProps, labelProps, errorProps, helpProps } = getAccessibleFormProps({
      id: props.id,
      name: props.name,
      required,
      error,
      errorMessage,
      helpText,
      'aria-label': props['aria-label'],
    });

    // Auto-resize functionality
    useEffect(() => {
      if (autoResize && internalRef.current) {
        const textarea = internalRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (showCount) {
        setCharCount(e.target.value.length);
      }
      if (onChange) {
        onChange(e);
      }
    };

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[60px]',
      md: 'text-base px-4 py-2 min-h-[100px]',
      lg: 'text-lg px-5 py-3 min-h-[140px]',
    };

    const textareaClasses = cn(
      'w-full rounded-md border transition-colors resize-vertical',
      'bg-base-100 text-base-content placeholder:text-base-content/40',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      sizeClasses[size],
      error && 'border-error focus:border-error focus:ring-error',
      !error && 'border-base-300 focus:border-primary focus:ring-primary',
      autoResize && 'resize-none overflow-hidden',
      getFocusClasses(),
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label
            {...labelProps}
            className="block text-sm font-medium text-base-content mb-1"
          >
            {label}
            {required && <span className="text-error ml-1" aria-label="richiesto">*</span>}
          </label>
        )}
        
        <textarea
          ref={(node) => {
            internalRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          {...inputProps}
          {...props}
          value={value}
          maxLength={maxLength}
          onChange={handleChange}
          className={textareaClasses}
        />
        
        <div className="flex justify-between items-start mt-1">
          <div className="flex-1">
            {error && errorMessage && (
              <p
                {...errorProps}
                className="text-sm text-error"
              >
                {errorMessage}
              </p>
            )}
            
            {!error && helpText && (
              <p
                {...helpProps}
                className="text-sm text-base-content/60"
              >
                {helpText}
              </p>
            )}
          </div>
          
          {showCount && maxLength && (
            <p className="text-sm text-base-content/60 ml-2 flex-shrink-0">
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

TextareaComponent.displayName = 'Textarea';

export const Textarea = memo(TextareaComponent);

